/**
 * Capture Snapshot, as data: the design's lens table (rv3 in capture.js) built from the
 * real routes. Pure functions, so the tests can check the arithmetic without a screen.
 *
 *   paid-trend      → Practitioner bills (paid + denied), Building's share, Paid, Denied, 12 mo
 *   summary byCode  → Enrolled / eligible and Not billed (the opportunity line, MODELED)
 *   fee schedule    → each code's name
 *
 * What the back end does not send stays empty, never guessed: days to pay on claims with no
 * remittance date, the wing breakdown (claims carry no wing).
 */
import type {
  CapturePaidTrend,
  CapturePaidTrendRow,
  CapturePayerClass,
  CaptureShareAgreement,
  CaptureSummaryV3,
  FeeScheduleResponse,
} from '@aithora/shared';
import { monthOf, type SigTone } from './lib';

export type Lens = 'code' | 'payer' | 'prac' | 'wing';
export type PayerPick = 'All' | 'FFS' | 'MA' | 'Medicaid' | 'Comm';
export type Period = 'Month' | 'Quarter' | '12 mo';
export type SortKey = 'name' | 'cap' | 'bill' | 'share' | 'paid' | 'den' | 'dtp' | 'g' | 'gap' | 'sig';

export const PERIOD_MONTHS: Record<Period, number> = { Month: 1, Quarter: 3, '12 mo': 12 };

/** The design's payer chips, as the trend's payer classes. */
export const PAYER_OF: Record<Exclude<PayerPick, 'All'>, CapturePayerClass[]> = {
  FFS: ['medicare'],
  MA: ['medicare_advantage'],
  Medicaid: ['medicaid'],
  Comm: ['commercial'],
};
export const PAYER_NAME: Record<CapturePayerClass, string> = {
  medicare: 'Medicare FFS',
  medicare_advantage: 'Medicare Advantage',
  medicaid: 'Medicaid',
  commercial: 'Commercial',
  other: 'Other payers',
  unknown: 'Payer not on file',
};

export interface LensRow {
  id: string;
  name: string;
  sub: string;
  /** The sub line's parts (each its own text, so each translates on its own). */
  subParts: string[];
  enr: number | null;
  elig: number | null;
  /** Cents, over the period. */
  bill: number;
  /** Null when no agreement governs the money (shareReason says why). */
  share: number | null;
  shareReason: string | null;
  paid: number;
  /** Denied claims as a share of claims back from the payer, in percent; null with none. */
  den: number | null;
  /** Days from service to remittance over the period's paid lines; null with none dated. */
  dtp: number | null;
  /** Billed cents per month, oldest first (the 12 mo sparkline). */
  trend: number[];
  /** Not billed, cents over the period: delivered minus billed (notBilled); with no care documented, the summary's opportunity line; null when unknown. */
  gap: number | null;
  sig: { text: string; k: SigTone } | null;
  /** Documented and billable care this month (the summary's measured line), cents over the period. */
  delivered: number | null;
  /** For the rail: the code itself (code lens), the payer class or the NPI. */
  key: string;
}

const codeKeyOf = (r: CapturePaidTrendRow) => r.code ?? r.codeKey ?? 'unknown';

/** The months a period covers, newest last (the trend's own month list). */
export function periodMonths(trend: CapturePaidTrend, per: Period): string[] {
  return trend.months.slice(-PERIOD_MONTHS[per]);
}

interface Acc {
  name: string;
  sub: string;
  parts: string[];
  bill: number;
  /** Billing on claims matched to no resident (the only billing Not billed subtracts). */
  ubill: number;
  paid: number;
  shareC: number;
  shareMissing: string | null;
  paidClaims: number;
  deniedClaims: number;
  payDays: number;
  payDated: number;
  byMonth: Map<string, number>;
}

/**
 * The lens table's rows for one lens, payer pick and period. The building's share is a
 * figure only when every cent of the row's money has an agreement; otherwise its reason
 * ("No agreement on file") is shown in place of a number.
 */
export function lensRows(
  lens: Lens,
  trend: CapturePaidTrend | null,
  opts: {
    pt: PayerPick;
    per: Period;
    summary?: CaptureSummaryV3 | null;
    fee?: FeeScheduleResponse | null;
    agreements?: CaptureShareAgreement[] | null;
  },
): LensRow[] {
  if (!trend || lens === 'wing') return [];
  const months = new Set(periodMonths(trend, opts.per));
  const mult = PERIOD_MONTHS[opts.per];
  const allowed = opts.pt === 'All' ? null : new Set(PAYER_OF[opts.pt]);
  const names = new Map<string, { name: string | null; category: string | null }>();
  for (const c of opts.fee?.codes ?? []) if (c.code) names.set(c.code, { name: c.name, category: c.category });
  const acc = new Map<string, Acc>();
  for (const r of trend.rows) {
    if (allowed && !allowed.has(r.payerClass)) continue;
    const key = lens === 'code' ? codeKeyOf(r) : lens === 'payer' ? r.payerClass : r.practitionerNpi ?? 'none';
    let a = acc.get(key);
    if (!a) {
      const nm = lens === 'code' ? names.get(key) : null;
      a = {
        name: lens === 'code' ? (r.code ?? 'Code hidden') : lens === 'payer' ? PAYER_NAME[r.payerClass] : r.practitionerName ?? (r.practitionerNpi ? 'NPI ' + r.practitionerNpi : 'No practitioner on the claim'),
        parts: lens === 'code' ? ([nm?.name, nm?.category].filter(Boolean) as string[]) : [lens === 'payer' ? payerSub(r.payerClass, opts.fee) : agreementSub(r.practitionerNpi, opts.agreements)],
        sub: '',
        bill: 0, ubill: 0, paid: 0, shareC: 0, shareMissing: null, paidClaims: 0, deniedClaims: 0, payDays: 0, payDated: 0, byMonth: new Map(),
      };
      a.sub = a.parts.join(' · ');
      acc.set(key, a);
    }
    const billed = r.paidCents + r.deniedCents;
    a.byMonth.set(r.period, (a.byMonth.get(r.period) ?? 0) + billed);
    if (!months.has(r.period)) continue;
    a.bill += billed;
    a.ubill += r.unmatchedBilledCents ?? 0;
    a.paid += r.paidCents;
    a.paidClaims += r.paidClaims;
    a.deniedClaims += r.deniedClaims;
    a.payDays += r.payDays ?? 0;
    a.payDated += r.payDated ?? 0;
    if (r.buildingShare.cents == null) {
      if (r.paidCents > 0) a.shareMissing = a.shareMissing ?? r.buildingShare.reason ?? 'No agreement on file';
    } else a.shareC += r.buildingShare.cents;
  }
  const lines = opts.summary?.byCode ?? [];
  const lineFor = (code: string, basis: 'measured' | 'opportunity') => lines.find((l) => (l.code ?? l.maskedKey) === code && l.basis === basis) ?? null;
  const out: LensRow[] = [];
  for (const [key, a] of acc) {
    const back = a.paidClaims + a.deniedClaims;
    const den = back ? (a.deniedClaims / back) * 100 : null;
    const meas = lens === 'code' ? lineFor(key, 'measured') : null;
    const opp = lens === 'code' ? lineFor(key, 'opportunity') : null;
    // Enrolled: residents with a signed billing election on file (the server's count), never
    // every resident the care was documented for.
    const enr = lens === 'code' && (meas || opp) ? enrolledOf(meas) + enrolledOf(opp) : null;
    const elig = lens === 'code' && (meas || opp) ? (meas?.residents ?? 0) + (opp?.residents ?? 0) : null;
    const delivered = meas ? meas.monthlyCents * mult : null;
    // Only unmatched billing comes off: a matched claim's code is already out of delivered (AF-1668).
    const gap = notBilled(delivered, a.ubill, opp ? opp.monthlyCents * mult : null);
    const share = a.shareMissing ? null : a.shareC;
    out.push({
      id: lens + ':' + key,
      key,
      name: a.name,
      sub: a.sub,
      subParts: a.parts,
      enr,
      elig,
      bill: a.bill,
      share,
      shareReason: a.shareMissing,
      paid: a.paid,
      den,
      dtp: a.payDated ? Math.round(a.payDays / a.payDated) : null,
      trend: trend.months.map((m) => a.byMonth.get(m) ?? 0),
      gap,
      sig: signalOf({ shareReason: a.shareMissing, den, gap, enr, elig, bill: a.bill }),
      delivered,
    });
  }
  // Codes with care documented or eligible this month but nothing back from a payer yet.
  if (lens === 'code' && opts.pt === 'All') {
    const seen = new Set<string>();
    for (const line of lines) {
      const key = line.code ?? line.maskedKey;
      if (!key || acc.has(key) || seen.has(key)) continue;
      seen.add(key);
      const meas = lineFor(key, 'measured');
      const o = lineFor(key, 'opportunity') ?? { ...line, residents: 0, monthlyCents: 0 };
      const nm = o.code ? names.get(o.code) : null;
      const delivered = meas ? meas.monthlyCents * mult : null;
      // No claims at all: everything delivered is not billed.
      const gap = notBilled(delivered, 0, o.monthlyCents * mult) ?? 0;
      const enr = enrolledOf(meas) + enrolledOf(lineFor(key, 'opportunity'));
      const elig = (meas?.residents ?? 0) + o.residents;
      out.push({
        id: 'code:' + key, key, name: o.code ?? 'Code hidden', sub: subOf(nm, o, line).join(' · '), subParts: subOf(nm, o, line),
        enr, elig, bill: 0, share: null, shareReason: null, paid: 0, den: null, dtp: null,
        trend: trend.months.map(() => 0), gap, sig: signalOf({ shareReason: null, den: null, gap, enr, elig, bill: 0 }),
        delivered,
      });
    }
  }
  return numberHiddenCodes(out);
}

/**
 * Rows whose code is hidden all read "Code hidden": two in the same category ("Code hidden
 * SCREENING" twice) cannot be told apart, so each such pair or more is numbered in order
 * ("Code hidden 1", "Code hidden 2"). A hidden code alone in its category keeps the plain name.
 */
export function numberHiddenCodes(rows: LensRow[]): LensRow[] {
  const groups = new Map<string, LensRow[]>();
  for (const r of rows) {
    if (r.name !== 'Code hidden') continue;
    const k = r.subParts[0] ?? '';
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  for (const g of groups.values()) {
    if (g.length < 2) continue;
    g.sort((a, b) => a.key.localeCompare(b.key)).forEach((r, i) => { r.name = 'Code hidden ' + (i + 1); });
  }
  return rows;
}

/** Residents on a summary line with a signed billing election (0 when the server sends none). */
const enrolledOf = (l: { enrolled?: number } | null | undefined): number => (l ? Math.max(0, Number(l.enrolled ?? 0)) : 0);

/**
 * Not billed over the period: care delivered with no claim (the server's measured care already
 * leaves out every code a claim matched to the resident billed) minus billing on unmatched
 * claims only, never below 0. A code with no documented care falls back to the summary's opportunity line
 * (the profile qualifies, no proof yet); null when neither is known.
 */
export function notBilled(delivered: number | null, billed: number, opportunity: number | null): number | null {
  if (delivered != null) return Math.max(0, delivered - billed);
  return opportunity;
}

function payerSub(pc: CapturePayerClass, fee?: FeeScheduleResponse | null): string {
  if (pc === 'medicare') return 'PUBLISHED rates' + (fee?.schedule ? ' · PFS ' + fee.schedule.year : '');
  if (pc === 'medicare_advantage') return 'No published rate';
  return 'Rate from your remittances';
}

function agreementSub(npi: string | null, agreements?: CaptureShareAgreement[] | null): string {
  if (!npi) return 'No NPI on the claim';
  const a = (agreements ?? []).filter((x) => x.practitionerNpi === npi || x.practitionerNpi == null).sort((x, y) => Number(y.practitionerNpi != null) - Number(x.practitionerNpi != null))[0];
  if (!a) return 'NPI on file · No agreement on file';
  const pct = a.kind === 'employed' ? 100 : a.kind === 'none' ? 0 : a.sharePercent;
  return 'NPI on file · agreement ' + monthOf(a.effectiveFrom) + (pct == null ? '' : ' · ' + pct + '%');
}

/** One plain signal per row, the most pressing first; null when nothing stands out. */
export function signalOf(r: { shareReason: string | null; den: number | null; gap: number | null; enr: number | null; elig: number | null; bill: number }): LensRow['sig'] {
  if (r.shareReason) return { text: r.shareReason, k: 'a' };
  if (r.den != null && r.den >= 6) return { text: 'Denials ' + r.den.toFixed(1) + '%', k: 'r' };
  if (r.gap != null && r.gap > 0 && !r.bill && r.elig) return { text: (r.elig - (r.enr ?? 0)) + ' eligible, not started', k: 'b' };
  return null;
}

/** Sort as the design's heads do (val(), L261): the gap, the capture ratio, the name, or a column. */
export function sortRows(rows: LensRow[], sk: SortKey, sd: 1 | -1): LensRow[] {
  const val = (r: LensRow): number | string => {
    if (sk === 'gap') return r.gap ?? -1;
    if (sk === 'cap') return r.elig ? (r.enr ?? 0) / r.elig : -1;
    if (sk === 'name') return r.name;
    if (sk === 'sig') return r.sig?.text ?? '';
    if (sk === 'g') return r.trend.length > 1 ? r.trend[r.trend.length - 1] - r.trend[0] : 0;
    const v = r[sk];
    return v == null ? -1 : v;
  };
  return rows.slice().sort((a, b) => {
    const x = val(a);
    const y = val(b);
    return (x > y ? 1 : x < y ? -1 : 0) * sd;
  });
}

/** Search across the name and the line under it (design q filter, L260). */
export const matchQ = (r: LensRow, q: string) => !q || (r.name + ' ' + r.sub).toLowerCase().includes(q.toLowerCase());

/** The trend's month totals, oldest first: billed (paid + denied), unmatched (billed on claims matched to no resident), paid, share (null when any month's share is missing). */
export function monthTotals(trend: CapturePaidTrend | null) {
  if (!trend) return { billed: [] as number[], unmatched: [] as number[], paid: [] as number[], share: [] as (number | null)[], denRate: [] as (number | null)[] };
  const billed: number[] = [];
  const unmatched: number[] = [];
  const paid: number[] = [];
  const share: (number | null)[] = [];
  const denRate: (number | null)[] = [];
  for (const m of trend.months) {
    const t = trend.totals.find((x) => x.period === m);
    const p = t?.paid.value ?? 0;
    const d = t?.denied.value ?? 0;
    billed.push(p + d);
    paid.push(p);
    share.push(t ? t.buildingShare.value : null);
    const rows = trend.rows.filter((r) => r.period === m);
    unmatched.push(rows.reduce((s, r) => s + (r.unmatchedBilledCents ?? 0), 0));
    const pc = rows.reduce((s, r) => s + r.paidClaims, 0);
    const dc = rows.reduce((s, r) => s + r.deniedClaims, 0);
    denRate.push(pc + dc ? (dc / (pc + dc)) * 100 : null);
  }
  return { billed, unmatched, paid, share, denRate };
}

/**
 * Days to pay over the last n months (service date to remittance, paid lines with both dates),
 * the n months before, a 12-month series for the sparkline, and the slowest payer class in the
 * window. Null where no paid line carries both dates.
 */
export function daysToPay(trend: CapturePaidTrend | null, n: number) {
  const empty = { cur: null as number | null, prev: null as number | null, series: [] as number[], slowest: null as { payer: CapturePayerClass; days: number } | null };
  if (!trend) return empty;
  const avg = (months: string[]) => {
    const set = new Set(months);
    let d = 0;
    let c = 0;
    for (const r of trend.rows) if (set.has(r.period)) { d += r.payDays ?? 0; c += r.payDated ?? 0; }
    return c ? d / c : null;
  };
  const ms = trend.months;
  const win = ms.slice(-n);
  const byPayer = new Map<CapturePayerClass, [number, number]>();
  for (const r of trend.rows) {
    if (!win.includes(r.period) || !r.payDated) continue;
    const x = byPayer.get(r.payerClass) ?? [0, 0];
    byPayer.set(r.payerClass, [x[0] + (r.payDays ?? 0), x[1] + r.payDated]);
  }
  let slowest: { payer: CapturePayerClass; days: number } | null = null;
  for (const [payer, [d, c]] of byPayer) if (!slowest || d / c > slowest.days) slowest = { payer, days: d / c };
  const series = ms.map((m) => avg([m])).filter((v): v is number => v != null);
  return { cur: avg(win), prev: ms.length > n ? avg(ms.slice(-2 * n, -n)) : null, series, slowest };
}

/** The sum of the last n entries, and the n before them (for "▲ 9% vs last month"). */
export function lastTwo(a: number[], n: number): [number, number] {
  const cur = a.slice(-n).reduce((s, v) => s + v, 0);
  const prev = a.slice(-2 * n, -n).reduce((s, v) => s + v, 0);
  return [cur, prev];
}

/** "▲ 7%" / "▼ 3%", or null with nothing to compare. */
export function delta(cur: number, prev: number): { text: string; up: boolean } | null {
  if (!prev) return null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { text: (pct >= 0 ? '▲ ' : '▼ ') + Math.abs(pct) + '%', up: pct >= 0 };
}

/** The server's placeholder name on a masked line (a lone dash), never shown as a name. */
const MASK_DASH = /^[\u2013\u2014-]$/;
/** A code's name and category; a masked code shows its category only (never a placeholder dash). */
function subOf(nm: { name: string | null; category: string | null } | null | undefined, o: { name: string }, line: { category?: string | null }): string[] {
  const name = nm?.name ?? (o.name && !MASK_DASH.test(o.name) ? o.name : null);
  return [name, nm?.category ?? line.category ?? null].filter((x): x is string => !!x);
}
